import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Volume2, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceMode – full voice dialogue component.
 *
 * Props:
 *  - sessionId: current chat session ID
 *  - chatEndpoint: full URL to the chat POST endpoint (e.g., `${API}/demo/sessions/${id}/chat`)
 *  - onNewMessages: callback(messages[]) called after each exchange so parent can refresh UI
 *  - disabled: disables voice mode (e.g., when session is completed)
 */
export const VoiceMode = ({ sessionId, chatEndpoint, onNewMessages, disabled = false, voice = 'nova', language = 'el', autoStart = false, autoStartSpeakText = '', onPhaseChange, sharedAudioRef = null }) => {
  const [active, setActive] = useState(false);        // voice mode on/off
  const [phase, setPhase] = useState('idle');           // idle | listening | processing | speaking
  const [transcript, setTranscript] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const activeRef = useRef(false);
  const autoStartedRef = useRef(false);
  const autoStartTextRef = useRef('');
  const bargeStreamRef = useRef(null);
  const bargeCtxRef = useRef(null);
  const bargeTimerRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { autoStartTextRef.current = autoStartSpeakText; }, [autoStartSpeakText]);
  // Reuse the single audio element the parent unlocked within the user's first
  // tap — this is what makes TTS play reliably on iOS/Android for every turn.
  useEffect(() => {
    if (sharedAudioRef && sharedAudioRef.current) { audioRef.current = sharedAudioRef.current; }
  }, [sharedAudioRef]);

  // Always resolve to the shared (gesture-unlocked) element when available.
  const getAudioEl = useCallback(() => {
    if (sharedAudioRef && sharedAudioRef.current) { audioRef.current = sharedAudioRef.current; }
    if (!audioRef.current) { audioRef.current = new Audio(); audioRef.current.preload = 'auto'; }
    return audioRef.current;
  }, [sharedAudioRef]);
  // Expose the current voice phase to the parent (for a prominent listening indicator)
  useEffect(() => { if (onPhaseChange) onPhaseChange(active ? phase : 'idle'); }, [phase, active, onPhaseChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopEverything();
    };
  }, []);

  const stopEverything = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    stopBargeMonitor();
    chunksRef.current = [];
  }, []);

  // ─── Barge-in: listen for the user talking WHILE the AI speaks, then interrupt ───
  const stopBargeMonitor = useCallback(() => {
    if (bargeTimerRef.current) { clearTimeout(bargeTimerRef.current); bargeTimerRef.current = null; }
    if (bargeStreamRef.current) {
      bargeStreamRef.current.getTracks().forEach(t => t.stop());
      bargeStreamRef.current = null;
    }
    if (bargeCtxRef.current) {
      try { bargeCtxRef.current.close(); } catch (e) { /* noop */ }
      bargeCtxRef.current = null;
    }
  }, []);

  const startBargeMonitor = useCallback(async (onBargeIn) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      // If speaking already ended before mic opened, abort
      if (!activeRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      bargeStreamRef.current = stream;

      const ctx = new AudioContext();
      bargeCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);
      let loudStart = null;
      const BARGE_THRESHOLD = 35;  // higher than silence (15) to ignore echo/residual AI audio
      const BARGE_SUSTAIN = 350;   // ms of sustained speech → treat as interruption

      const check = () => {
        if (!bargeStreamRef.current) return;
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        if (avg > BARGE_THRESHOLD) {
          if (!loudStart) loudStart = Date.now();
          if (Date.now() - loudStart > BARGE_SUSTAIN) {
            onBargeIn();
            return;
          }
        } else {
          loudStart = null;
        }
        bargeTimerRef.current = setTimeout(check, 80);
      };
      // Small delay so the AI's first syllables don't self-trigger
      bargeTimerRef.current = setTimeout(check, 300);
    } catch (e) {
      // No mic available for barge-in — AI simply finishes speaking
    }
  }, []);

  // ─── Unlock audio playback within the user gesture (defeats autoplay blocking) ───
  const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=';
  const unlockAudio = useCallback(() => {
    try {
      const el = getAudioEl();
      el.src = SILENT_WAV;
      el.muted = true;
      const p = el.play();
      if (p && p.then) {
        p.then(() => { el.pause(); el.currentTime = 0; el.muted = false; })
         .catch(() => { el.muted = false; });
      }
    } catch (e) {
      console.warn('Audio unlock failed:', e);
    }
  }, [getAudioEl]);

  // ─── Toggle voice mode ───
  const toggleVoice = useCallback(() => {
    if (active) {
      // Turn off
      setActive(false);
      setPhase('idle');
      stopEverything();
    } else {
      // Turn on
      setActive(true);
      unlockAudio();
      startListening();
    }
  }, [active, stopEverything, unlockAudio]);

  // ─── 1. Start listening (record from mic) ───
  const startListening = useCallback(async () => {
    try {
      setPhase('listening');
      setTranscript('');
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      // Silence detection via AnalyserNode
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size > 0 && activeRef.current) {
          processAudio(blob);
        }
      };

      recorder.start(250); // collect chunks every 250ms

      // Start silence detection
      detectSilence(analyser, () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          // Stop mic stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
          }
        }
      });

    } catch (err) {
      console.error('Mic access error:', err);
      toast.error('Microphone access denied');
      setActive(false);
      setPhase('idle');
    }
  }, []);

  // ─── Silence detection ───
  const detectSilence = useCallback((analyser, onSilence) => {
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let silenceStart = null;
    const SILENCE_THRESHOLD = 15;
    const SILENCE_DURATION = 1800; // 1.8s of silence → stop

    const check = () => {
      if (!activeRef.current) return;

      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

      if (avg < SILENCE_THRESHOLD) {
        if (!silenceStart) silenceStart = Date.now();
        if (Date.now() - silenceStart > SILENCE_DURATION) {
          onSilence();
          return;
        }
      } else {
        silenceStart = null;
      }

      silenceTimerRef.current = setTimeout(check, 100);
    };

    // Give user 500ms to start speaking before checking silence
    silenceTimerRef.current = setTimeout(check, 500);
  }, []);

  // ─── 2. Process audio: transcribe → chat → speak ───
  const processAudio = useCallback(async (blob) => {
    if (!activeRef.current) return;
    setPhase('processing');

    try {
      // Transcribe (hint the form language so Greek stays Greek, never translated)
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      formData.append('language', language || 'el');
      const sttRes = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      const userText = sttRes.data.text;
      if (!userText || !userText.trim()) {
        // Empty transcription – go back to listening
        if (activeRef.current) startListening();
        return;
      }

      setTranscript(userText);

      // Send to chat
      const chatRes = await axios.post(chatEndpoint, { content: userText });
      const aiMessage = chatRes.data.message;

      // Notify parent to refresh messages
      if (onNewMessages) onNewMessages();

      // If session completed, stop voice mode
      if (chatRes.data.session_status === 'completed') {
        await speakText(aiMessage);
        setActive(false);
        setPhase('idle');
        return;
      }

      // Speak AI response
      await speakText(aiMessage);

      // Continue listening loop
      if (activeRef.current) {
        startListening();
      }

    } catch (err) {
      console.error('Voice processing error:', err);
      toast.error('Voice processing failed');
      if (activeRef.current) startListening();
    }
  }, [chatEndpoint, onNewMessages, startListening, language]);

  // ─── 3. Speak text via TTS ───
  const speakText = useCallback(async (text) => {
    if (!text) return;
    setPhase('speaking');

    let audioUrl = null;
    try {
      // Clean text: remove RESPONSE[] markers before speaking
      const cleanText = text.replace(/RESPONSE\[.*?\]=.*$/gm, '').trim();
      if (!cleanText) return;

      const res = await axios.post(`${API}/voice/synthesize`,
        { text: cleanText, voice: voice },
        { responseType: 'blob', timeout: 30000 }
      );

      if (!res.data || res.data.size === 0) {
        console.error('TTS returned empty audio blob');
        return;
      }

      audioUrl = URL.createObjectURL(res.data);
      // Reuse the shared element the parent unlocked within the user gesture.
      const audio = getAudioEl();
      audio.muted = false;
      audio.src = audioUrl;

      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          if (audioUrl) URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onended = finish;
        audio.onerror = () => {
          console.error('Audio element error:', audio.error);
          finish();
        };
        const p = audio.play();
        if (p && p.catch) {
          p.catch((err) => {
            console.error('audio.play() failed:', err?.name, err?.message);
            // Autoplay was blocked — let the user tap once to enable sound
            if (err && err.name === 'NotAllowedError') {
              toast.error('Πατήστε οπουδήποτε για να ενεργοποιηθεί η φωνή');
              const resume = () => {
                audio.play().catch(() => {});
                document.removeEventListener('click', resume);
                document.removeEventListener('touchstart', resume);
              };
              document.addEventListener('click', resume, { once: true });
              document.addEventListener('touchstart', resume, { once: true });
            }
            finish();
          });
        }
        // NOTE: the microphone is intentionally kept CLOSED while the AI speaks
        // (no barge-in) to avoid recording the AI's own audio / echo / silence.
        // Listening only resumes after playback fully ends (see processAudio).
      });

    } catch (err) {
      console.error('TTS request error:', err?.response?.status, err?.message);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      // Don't block the flow if TTS fails
    }
  }, [voice, getAudioEl]);

  // ─── Auto-start voice mode (hands-free): speak the first question, then listen ───
  // The shared audio element was already unlocked by the parent within the user's
  // consent/resume tap, so we do NOT call unlockAudio() here (it would race the
  // real TTS playback). We go straight to speaking, then open the mic.
  const activateAuto = useCallback(async () => {
    setActive(true);
    activeRef.current = true;
    const firstText = autoStartTextRef.current;
    if (firstText) {
      await speakText(firstText);
    }
    if (activeRef.current) {
      startListening();
    }
  }, [speakText, startListening]);

  useEffect(() => {
    if (autoStart && !autoStartedRef.current && sessionId && !disabled && !active) {
      autoStartedRef.current = true;
      activateAuto();
    }
  }, [autoStart, sessionId, disabled, active, activateAuto]);

  // Phase labels
  const phaseLabel = {
    idle: '',
    listening: 'Listening...',
    processing: 'Processing...',
    speaking: 'Speaking...',
  };

  return (
    <div className="flex items-center gap-2" data-testid="voice-mode-container">
      {/* Voice toggle button */}
      <Button
        type="button"
        onClick={toggleVoice}
        disabled={disabled}
        className={`rounded-full transition-all duration-300 ${
          active
            ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30'
            : 'bg-[#1E293B] hover:bg-[#334155] border border-[#334155]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{ width: 44, height: 44, padding: 0 }}
        aria-label={active ? 'Stop voice mode' : 'Start voice mode'}
        data-testid="voice-mode-toggle"
      >
        {active ? (
          <MicOff className="h-5 w-5 text-white" />
        ) : (
          <Mic className="h-5 w-5 text-white" />
        )}
      </Button>

      {/* Voice status overlay */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="flex items-center gap-2"
            data-testid="voice-mode-status"
          >
            {/* Animated indicator */}
            {phase === 'listening' && (
              <div className="flex items-center gap-1" data-testid="voice-listening-indicator">
                {[0, 1, 2, 3, 4].map(i => (
                  <motion.div
                    key={`item-${i}`}
                    className="w-1 bg-red-400 rounded-full"
                    animate={{ height: [4, 16, 4] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.1 }}
                  />
                ))}
              </div>
            )}
            {phase === 'processing' && (
              <Loader2 className="h-4 w-4 text-[#3B82F6] animate-spin" data-testid="voice-processing-indicator" />
            )}
            {phase === 'speaking' && (
              <div className="flex items-center gap-1" data-testid="voice-speaking-indicator">
                <Volume2 className="h-4 w-4 text-emerald-400" />
                {[0, 1, 2].map(i => (
                  <motion.div
                    key={`item-${i}`}
                    className="w-1 bg-emerald-400 rounded-full"
                    animate={{ height: [3, 12, 3] }}
                    transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.15 }}
                  />
                ))}
              </div>
            )}

            <span className="text-xs text-[#94A3B8] whitespace-nowrap">
              {phaseLabel[phase]}
            </span>

            {transcript && phase === 'processing' && (
              <span className="text-xs text-[#64748B] truncate max-w-[160px]" data-testid="voice-transcript">
                "{transcript}"
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
